import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Switch,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridRowsProp,
} from "@mui/x-data-grid";

import { LoadingScreen } from "../../ui/components/LoadingScreen";
import CustomWarningModal from "../public/CustomWarningModal";
import CustomEditModal from "../public/CustomEditModal";
import {
  createNewTicket,
  updateTicket,
  deleteTicket,
} from "../../api/providers/plannerEventsProviders";
import { Delete, Edit } from "@mui/icons-material";

interface TicketVariant {
  id: number;
  name: string;
  status: boolean;
  startDate: string;
  closeDate: string;
  currency: string;
  details: string;
  price: number;
  quantityAvailable: number;
}

interface CurrentTicketData {
  name: string;
  status: boolean;
  startDate: string;
  closeDate: string;
  details: string;
  price: number;
  quantityAvailable: number;
}

interface PlannerEventTicketsProps {
  ticketVariants: TicketVariant[] | undefined;
  eventId: number;
  selectedEventData: () => void;
}

export const PlannerEventTickets: React.FC<PlannerEventTicketsProps> = ({
  ticketVariants,
  eventId,
  selectedEventData,
}) => {
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [price, setPrice] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [selectedTicketData, setSelectedTicketData] =
    useState<CurrentTicketData>({
      name: "",
      status: false,
      startDate: "",
      closeDate: "",
      details: "",
      price: 0,
      quantityAvailable: 0,
    });
  const [selectedTicketId, setSelectedTicketId] = useState();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);

  const handleCreateTicket = async () => {
    try {
      setLoading(true);
      const result = await createNewTicket({
        eventId: eventId,
        name: name,
        quantityAvailable: quantity,
        details: details,
        price: price,
        currency: currency,
        startDate: startDate || "",
        closeDate: endDate || "",
      });
      if (result.ok) {
        selectedEventData();
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTicket = async () => {
    try {
      setLoading(true);
      const result = await deleteTicket(selectedTicketId);
      if (result.ok) {
        setShowWarningModal(false);
        selectedEventData();
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTicket = async () => {
    try {
      setLoading(true);

      const result = await updateTicket(selectedTicketId, selectedTicketData);
      if (result.ok) {
        setShowWarningModal(false);
        selectedEventData();
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setSelectedTicketId(id);
    setShowWarningModal(true);
  };

  const handleEditClick = (
    id: number,
    name: string,
    details: string,
    status: boolean,
    startDate: string,
    closeDate: string,
    price: number,
    quantityAvailable: number
  ) => {
    setSelectedTicketId(id);
    setSelectedTicketData({
      name,
      details,
      status,
      startDate,
      closeDate,
      price,
      quantityAvailable,
    });
    setShowEditModal(true);
  };

  const columns: GridColDef[] = [
    { field: "name", headerName: "Nombre", width: 200 },
    { field: "details", headerName: "Detalles", width: 220 },
    {
      field: "currency",
      headerName: "Moneda",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "price",
      headerName: "Precio",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "quantityAvailable",
      headerName: "Cantidad",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "startDate",
      headerName: "Fecha de Inicio",
      width: 150,
      type: "date",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "closeDate",
      headerName: "Fecha de Cierre",
      width: 150,
      type: "date",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "status",
      headerName: "Estado",
      width: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridCellParams) => (
        <Switch
          checked={params.value === "activo"}
          /*  onChange={(event) =>
             handleToggleState(params.id, event.target.checked)
           } */
          color="primary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <IconButton
           
            onClick={() =>
              handleEditClick(
                +params.id,
                params.row.name,
                params.row.details,
                params.row.status,
                dayjs(params.row.startDate).format("YYYY-MM-DD"),
                dayjs(params.row.closeDate).format("YYYY-MM-DD"),
                params.row.price,
                params.row.quantityAvailable
              )
            }
          >
            <Edit />
          </IconButton>
          <IconButton
            sx={{ color: "secondaryAccent.dark" }}
            onClick={() => handleDeleteClick(+params.id)}
          >
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const rows: GridRowsProp =
    ticketVariants?.map((variant) => ({
      id: variant.id,
      name: variant.name,
      status: variant.status,
      startDate: new Date(variant.startDate.split("Z")[0]),
      closeDate: new Date(variant.closeDate.split("Z")[0]),
      currency: variant.currency,
      details: variant.details,
      price: variant.price,
      quantityAvailable: variant.quantityAvailable,
    })) || [];

  const handleStateChange = (event: any) => {
    setCurrency(event.target.value);
  };

  const handleCancelButton = () => {
    return setShowCreateTicket(false);
  };

  const handleCloseWarningModal = () => {
    return setShowWarningModal(false);
  };

  const handleCloseEditModal = () => {
    return setShowEditModal(false);
  };

  const handleEditTicketData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.target;
    setSelectedTicketData((prevState) => {
      const newValue = type === "checkbox" ? checked : value;
      return {
        ...prevState,
        [name]:
          name === "price" || name === "quantityAvailable"
            ? Number(newValue)
            : newValue,
      };
    });
  };

  const handleEditStartDateChange = (date: any) => {
    setSelectedTicketData((prevState) => ({
      ...prevState,
      startDate: date ? dayjs(date).format("YYYY-MM-DD") : "",
    }));
  };

  const handleEditEndDateChange = (date: any) => {
    setSelectedTicketData((prevState) => ({
      ...prevState,
      closeDate: date ? dayjs(date).format("YYYY-MM-DD") : "",
    }));
  };

  const handleStartDateChange = (newValue: any) => {
    if (newValue) {
      const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
      setStartDate(formattedDate);
    } else {
      setStartDate(null);
    }
  };

  const handleEndDateChange = (newValue: any) => {
    if (newValue) {
      const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
      setEndDate(formattedDate);
    } else {
      setEndDate(null);
    }
  };

  return (
    <Box>
      {loading && <LoadingScreen />}

      <Grid container alignItems="center">
        {showCreateTicket ? (
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Crear nueva entrada
          </Typography>
        ) : (
          <Grid item>
            <Button
              variant="contained"
              sx={{
                bgcolor: "primary.light",
                borderRadius: 1,
                my: 1,
                width: 200,
              }}
              onClick={() => setShowCreateTicket(true)}
            >
              Agregar Entrada
            </Button>
          </Grid>
        )}
      </Grid>

      {showCreateTicket && (
        <>
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre de la entrada"
                fullWidth
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <TextField
                label="Detalles"
                fullWidth
                variant="outlined"
                multiline
                rows={4}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                sx={{ mt: 2 }}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha de inicio"
                  value={startDate ? dayjs(startDate) : null}
                  onChange={handleStartDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  sx={{ mt: 2, mr: 2 }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha de fin"
                  value={endDate ? dayjs(endDate) : null}
                  onChange={handleEndDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  sx={{ mt: 2 }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Precio"
                fullWidth
                variant="outlined"
                value={price}
                onChange={(e) => setPrice(+e.target.value)}
              />

              <TextField
                label="Cantidad disponible"
                fullWidth
                variant="outlined"
                value={quantity}
                onChange={(e) => setQuantity(+e.target.value)}
                sx={{ mt: 2 }}
              />

              <TextField
                select
                label="Moneda"
                fullWidth
                variant="outlined"
                value={currency}
                onChange={handleStateChange}
                sx={{ mt: 2 }}
              >
                <MenuItem value="USD">USD</MenuItem>
              </TextField>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                sx={{ bgcolor: "primary.light", borderRadius: 1, mr: 2 }}
                onClick={() => handleCreateTicket()}
              >
                Guardar
              </Button>
              <Button
                variant="contained"
                sx={{ bgcolor: "secondaryAccent.dark", borderRadius: 1 }}
                onClick={handleCancelButton}
              >
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </>
      )}

      <Box sx={{ height: "60vh", width: "100%", mt: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          sx={{ padding: "10px" }}
          localeText={{
            noRowsLabel: "No hay filas",
            noResultsOverlayLabel: "No se encontraron resultados.",
            toolbarColumns: "Columnas",
            toolbarColumnsLabel: "Selecciona columnas",
            toolbarFilters: "Filtros",
            toolbarFiltersLabel: "Mostrar filtros",
            toolbarFiltersTooltipHide: "Ocultar filtros",
            toolbarFiltersTooltipShow: "Mostrar filtros",
            filterPanelAddFilter: "Agregar filtro",
            filterPanelRemoveAll: "Eliminar todos",
            filterPanelDeleteIconLabel: "Eliminar",
            filterPanelLogicOperator: "Operador lógico",
            filterPanelOperatorAnd: "Y",
            filterPanelOperatorOr: "O",
            filterPanelColumns: "Columnas",
            filterPanelInputLabel: "Valor",
            filterPanelInputPlaceholder: "Filtrar valor",
            toolbarQuickFilterPlaceholder: "Buscar",
            toolbarExport: "Exportar",
            toolbarExportLabel: "Exportar",
            toolbarExportCSV: "Descargar como CSV",
            toolbarExportPrint: "Imprimir",
            toolbarDensity: "Densidad",
            toolbarDensityLabel: "Densidad",
            toolbarDensityCompact: "Compacta",
            toolbarDensityStandard: "Estándar",
            toolbarDensityComfortable: "Cómoda",
            columnMenuLabel: "Menú",
            columnMenuShowColumns: "Mostrar columnas",
            columnMenuFilter: "Filtrar",
            columnMenuHideColumn: "Ocultar",
            columnMenuUnsort: "Desordenar",
            columnMenuSortAsc: "Orden ascendente",
            columnMenuSortDesc: "Orden descendente",
          }}
        />
      </Box>
      <CustomWarningModal
        onPressCancel={handleCloseWarningModal}
        isVisible={showWarningModal}
        onPressConfirm={handleDeleteTicket}
        title="Estás seguro de eliminar esta entrada?"
      />
      <CustomEditModal
        isVisible={showEditModal}
        onPressConfirm={handleUpdateTicket}
        onPressCancel={handleCloseEditModal}
      >
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nombre de la entrada"
              name="name"
              fullWidth
              variant="outlined"
              value={selectedTicketData.name}
              onChange={(e) => handleEditTicketData(e)}
            />

            <TextField
              label="Detalles"
              fullWidth
              name="details"
              variant="outlined"
              multiline
              rows={4}
              value={selectedTicketData.details}
              onChange={(e) => handleEditTicketData(e)}
              sx={{ mt: 2 }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha de inicio"
                value={
                  selectedTicketData.startDate
                    ? dayjs(selectedTicketData.startDate)
                    : null
                }
                onChange={handleEditStartDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                sx={{ mt: 2, mr: 2 }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha de fin"
                value={
                  selectedTicketData.closeDate
                    ? dayjs(selectedTicketData.closeDate)
                    : null
                }
                onChange={handleEditEndDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                sx={{ mt: 2 }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Precio"
              name="price"
              fullWidth
              variant="outlined"
              value={selectedTicketData.price}
              onChange={(e) => handleEditTicketData(e)}
            />

            <TextField
              label="Cantidad disponible"
              name="quantityAvailable"
              fullWidth
              variant="outlined"
              value={selectedTicketData.quantityAvailable}
              onChange={(e) => handleEditTicketData(e)}
              sx={{ mt: 2 }}
            />
          </Grid>
        </Grid>
      </CustomEditModal>
    </Box>
  );
};

